import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Card, 
  Descriptions, 
  Button, 
  Tag, 
  Space, 
  message, 
  Spin, 
  Modal, 
  Form, 
  Select, 
  Input,
  Row,
  Col,
  Divider,
  Image
} from 'antd';
import { 
  ArrowLeftOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CheckOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  StoreDisplayGroupPlanogramApply,
  ApplyStatus,
  TeamType,
  APPLY_STATUS_COLORS,
  AuditStoreDisplayGroupPlanogramApplyRequest
} from '../../../types/storeDisplayGroupPlanogramApply';
import StoreDisplayGroupPlanogramApplyService from '../../../services/storeDisplayGroupPlanogramApplyService';
import { sanitizeErrorForDisplay } from '../../../utils/errorHandler';
import EmptyState from '../../../components/common/EmptyState';
import NetworkError from '../../../components/common/NetworkError';

const StoreDisplayGroupPlanogramApplyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [auditForm] = Form.useForm();

  // State management
  const [apply, setApply] = useState<StoreDisplayGroupPlanogramApply | null>(null);
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [auditModalVisible, setAuditModalVisible] = useState(false);

  // Load apply detail
  const loadApplyDetail = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const applyId = parseInt(id, 10);
      if (isNaN(applyId)) {
        throw new Error('无效的申请ID');
      }
      
      const response = await StoreDisplayGroupPlanogramApplyService.getApplyById(applyId);
      
      if (response.success && response.data) {
        setApply(response.data);
      } else {
        throw new Error(response.errorMessage || '获取申请详情失败');
      }
    } catch (error) {
      const errorMessage = sanitizeErrorForDisplay(error);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Initial data load
  useEffect(() => {
    loadApplyDetail();
  }, [loadApplyDetail]);

  // Handle back to list
  const handleBack = useCallback(() => {
    const returnTo = searchParams.get('returnTo');
    // 如果有returnTo参数，返回到指定页面；否则返回默认页面
    navigate(returnTo || '/sdg-planogram-apply');
  }, [navigate, searchParams]);

  // Handle audit apply
  const handleAudit = useCallback(() => {
    setAuditModalVisible(true);
    auditForm.resetFields();
  }, [auditForm]);

  // Handle audit submit
  const handleAuditSubmit = useCallback(async (values: any) => {
    if (!apply) return;
    
    setAuditLoading(true);
    try {
      const request: AuditStoreDisplayGroupPlanogramApplyRequest = {
        id: apply.id,
        applyStatus: values.applyStatus,
        auditRemark: values.auditRemark
      };
      
      const response = await StoreDisplayGroupPlanogramApplyService.auditApply(request);
      if (response.success) {
        message.success(t('storeDisplayGroupPlanogramApply.auditSuccess'));
        setAuditModalVisible(false);
        loadApplyDetail(); // Refresh data
      } else {
        message.error(response.errorMessage || t('storeDisplayGroupPlanogramApply.auditError'));
      }
    } catch (error) {
      message.error(t('storeDisplayGroupPlanogramApply.auditError'));
    } finally {
      setAuditLoading(false);
    }
  }, [apply, t, loadApplyDetail]);

  // Handle retry for network errors
  const handleRetry = useCallback(() => {
    loadApplyDetail();
  }, [loadApplyDetail]);

  // Format datetime display
  const formatDateTime = (dateTime?: string): string => {
    return dateTime ? dayjs(dateTime).format('YYYY-MM-DD HH:mm:ss') : '-';
  };

  // Format date display
  const formatDate = (date?: string): string => {
    return date ? dayjs(date).format('YYYY-MM-DD') : '-';
  };

  // Render apply status
  const renderApplyStatus = (status: ApplyStatus) => {
    const color = APPLY_STATUS_COLORS[status];
    return (
      <Tag color={color} style={{ fontSize: '14px', padding: '4px 8px' }}>
        {t(`storeDisplayGroupPlanogramApply.status.${status.toLowerCase()}`)}
      </Tag>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <NetworkError
        message={error}
        onRetry={handleRetry}
      />
    );
  }

  if (!apply) {
    return (
      <EmptyState
        description={t('storeDisplayGroupPlanogramApply.notFound')}
        action={
          <Button type="primary" onClick={handleBack}>
            {t('common.back')}
          </Button>
        }
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
          >
            {t('common.back')}
          </Button>
          <h2>{t('storeDisplayGroupPlanogramApply.detailTitle')}</h2>
        </Space>
        
        <Space>
          {apply.applyStatus === ApplyStatus.PENDING && (
            <>
              
              <Button 
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleAudit}
              >
                {t('storeDisplayGroupPlanogramApply.audit')}
              </Button>
            </>
          )}
        </Space>
      </div>

      <Row gutter={[24, 24]}>
        {/* Basic Information */}
        <Col span={24}>
          <Card title={t('storeDisplayGroupPlanogramApply.basicInfo')} bordered={false}>
            <Descriptions column={2} labelStyle={{ width: 120 }}>
              <Descriptions.Item label={t('storeDisplayGroupPlanogramApply.applyNo')}>
                <strong style={{ fontSize: '16px' }}>{apply.applyNo}</strong>
              </Descriptions.Item>
              
              <Descriptions.Item label={t('storeDisplayGroupPlanogramApply.applyStatus')}>
                {renderApplyStatus(apply.applyStatus)}
              </Descriptions.Item>
              
              <Descriptions.Item label={t('storeDisplayGroupPlanogramApply.planogramCode')}>
                {apply.planogramCode}
              </Descriptions.Item>
              
              {/* <Descriptions.Item label={t('storeDisplayGroupPlanogramApply.storeInfo')}>
                <div>
                  <div><strong>{apply.storeCode}</strong></div>
                  <div style={{ color: '#666', fontSize: '12px' }}>{apply.storeName}</div>
                </div>
              </Descriptions.Item> */}

              <Descriptions.Item label={t('storeDisplayGroupPlanogramApply.storeGroupName')}>
                <div>
                  <div><strong>{apply.storeCode}</strong></div>
                  <div style={{ color: '#666', fontSize: '12px' }}>{apply.storeGroupName}</div>
                </div>
              </Descriptions.Item>
              
              <Descriptions.Item label={t('storeDisplayGroupPlanogramApply.displayGroupInfo')}>
                <div>
                  <div><strong>{apply.displayGroupCode}</strong></div>
                  <div style={{ color: '#666', fontSize: '12px' }}>{apply.displayGroupName}</div>
                </div>
              </Descriptions.Item>
              
              {/* <Descriptions.Item label={t('storeDisplayGroupPlanogramApply.expectedEndDate')}>
                {formatDate(apply.expectedEndDate)}
              </Descriptions.Item> */}
              
              <Descriptions.Item label={t('storeDisplayGroupPlanogramApply.actualEndDate')}>
                {formatDate(apply.actualEndDate)}
              </Descriptions.Item>
            </Descriptions>
            
            {/* Planogram Image Display */}
            {apply.planogramImageUrl && (
              <div style={{ marginTop: '24px' }}>
                <h4 style={{ marginBottom: '12px', fontWeight: 'bold' }}>
                  {t('storeDisplayGroupPlanogramApply.planogramImageUrl')}
                </h4>
                <div style={{ textAlign: 'center' }}>
                  <Image
                    src={apply.planogramImageUrl}
                    alt={t('storeDisplayGroupPlanogramApply.planogramImageUrl')}
                    width={300}
                    height={200}
                    style={{ objectFit: 'cover', borderRadius: '8px', border: '1px solid #d9d9d9' }}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN4BMghRWmRqTAcAghcrZWcgRc+dGKZXKuIScAoSChFKBbpJUcO/t7GnsHRkCgM3p7hDSMmSvurDPO1yll5z57vTX6fYFEZ/d68LU/ey9wZx/Vy1TdNfTmOSs14j1+X6lOqXxrYpK9cG9SB1NnPzQ3q3rOzrOgfn9YBgAAIABJREFUeNrtXXtXU1fyPnvDcz8jOcZ4n/Wf8R7xHiPedmFe+A3OOdWdm3XOqe7Z2XN6du/vXauoqmpOdXVVf7ur+mun+oMGg8eEOdLv98tPf/7uSHd++PbhKSGOdLt7cBDdqFGjRo0aNWrUqFGjRo0aNWrUqFGjRo0aNWrUqFGjRo0aNWrUqFGjRo0aNWrUqFGjRo0aNWrUqFGjRo0aNWrUqFGzv9y5c0dtbGz4pz7p/fxQpZHfxu4qatSoUaNGjRo1atSoUaNGjRo1atSoUaNGjRo1atSoUaNGjRo1atSoUaNGjRo1atSoUaNGjZr95s8//8z+8RIxSv4f9/nwZlz5hzf8xJQTRfgr0v/fvXNOk/JO2PzOu/0jh4+8k8/+Zxqfcp2pP++eWHQyfKl/+eWXb3788cfv/vWvf/199+7dyf/973+v/u///rqWfneDcaJ6LH/e6nfvjzNhAAAAAAAAACAA4Nl/+OP7Pvv75r/vdyejZxxj3+8OM/cKTn0OYAKAHZ9v5I9v9u5MNsbzMjOvbgEAQTU5LLj7t23PN2vfv37wWI7sggEAAIACAOhWv37hP/qzv8nYgB+cAYz5nHzfjkfj+Kz7O/lODdp/68aH/Ol6yGN8fgz/+PHjN7/88ssP33///X/dvHnz5L179w42mHvW/fHRV+Xp0ckjD6ee1d9pAwDjZvBB4D7MRwcHnZlL71gIGnx+1LqNDfSv3//y0/de//58/Nc8P8ZGxoLRu/7WjQ/3nz9NiN/mIWzfHnfYWAjvTq6Pu46Hw9++MuTczOzD4eAb5o3e2WOgPnftJ3J5f+3P5Ox5P2aHwKOjw8Ej5S3Oau8z7/8aZgzbOacI4LvNPFfz5s2bJ8J8b15z7NjHW/WT3x8PdkIKhPh+f3a21eu1v+ZjRr/WgNOmAGo2Q0IInvZXLGvdp5sHfvRrJ+2Hh4fHT09PUdKP1pfkLpkwBQw/IuP1+q1uNkz1rU/1Q77/3z/lNg7HuCIqiLAH3oCN7EzXJdUwgKZJYX78/qw/Xj8Ye46Nj7f9y5c/b1m8Ot/5QJQV2IpB2JgHi3HznI6OYkO98P4I/fK5+LqEVXpD6IWpQjy+zfLX/YjDO3bYr7b8A3v6avG9e/e+PxOIwPb/xpNY+BevPr0+cfWKT8Q7Ly5PT09f33r/+s93jyJlXJPCFkq2DDFN56eMfGNgbKCZEaENGXfWZBk4ff7Zn6uJW7KCVmTdfLp40xeIjh07sj+8mFO1/2wFDhYDvC9Tt/aW19yfvdY1Yg=="
                  />
                </div>
              </div>
            )}
          </Card>
        </Col>

        {/* Apply Information */}
        <Col span={12}>
          <Card title={t('storeDisplayGroupPlanogramApply.applyInfo')} bordered={false}>
            <Descriptions column={1} labelStyle={{ width: 120 }}>
              <Descriptions.Item label={t('storeDisplayGroupPlanogramApply.applyPerson')}>
                <div>
                  <div><strong>{apply.applyPersonName}</strong></div>
                  <div style={{ color: '#666', fontSize: '12px' }}>{apply.applyPerson}</div>
                </div>
              </Descriptions.Item>
              
              <Descriptions.Item label={t('storeDisplayGroupPlanogramApply.applyTime')}>
                {formatDateTime(apply.applyTime)}
              </Descriptions.Item>
              
              <Descriptions.Item label={t('storeDisplayGroupPlanogramApply.applyReason')}>
                <div style={{ 
                  padding: '8px 12px', 
                  background: '#fafafa', 
                  borderRadius: '4px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {apply.applyReason}
                </div>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Audit Information */}
        <Col span={12}>
          <Card 
            title={t('storeDisplayGroupPlanogramApply.auditInfo')} 
            bordered={false}
            style={{
              borderLeft: apply.auditPerson ? (
                apply.applyStatus === ApplyStatus.APPROVED ? '4px solid #52c41a' :
                apply.applyStatus === ApplyStatus.REJECTED ? '4px solid #ff4d4f' : '4px solid #d9d9d9'
              ) : '4px solid #d9d9d9'
            }}
          >
            {apply.auditPerson ? (
              <Descriptions column={1} labelStyle={{ width: 120 }}>
                <Descriptions.Item label={t('storeDisplayGroupPlanogramApply.auditPerson')}>
                  <div>
                    <div><strong>{apply.auditPersonName}</strong></div>
                    <div style={{ color: '#666', fontSize: '12px' }}>{apply.auditPerson}</div>
                  </div>
                </Descriptions.Item>
                
                <Descriptions.Item label={t('storeDisplayGroupPlanogramApply.auditTime')}>
                  {formatDateTime(apply.auditTime)}
                </Descriptions.Item>
                
                <Descriptions.Item label={t('storeDisplayGroupPlanogramApply.auditResult')}>
                  {renderApplyStatus(apply.applyStatus)}
                </Descriptions.Item>
                
                {apply.auditRemark && (
                  <Descriptions.Item label={t('storeDisplayGroupPlanogramApply.auditRemark')}>
                    <div style={{ 
                      padding: '8px 12px', 
                      background: '#fafafa', 
                      borderRadius: '4px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {apply.auditRemark}
                    </div>
                  </Descriptions.Item>
                )}
              </Descriptions>
            ) : (
              <div style={{ textAlign: 'center', color: '#999', padding: '40px 0' }}>
                {t('storeDisplayGroupPlanogramApply.notAudited')}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Audit Modal */}
      <Modal
        title={t('storeDisplayGroupPlanogramApply.auditTitle')}
        open={auditModalVisible}
        onCancel={() => setAuditModalVisible(false)}
        footer={null}
        width={600}
      >
        <div>
          <div style={{ marginBottom: 16, padding: 16, background: '#fafafa', borderRadius: 4 }}>
            <div><strong>{t('storeDisplayGroupPlanogramApply.applyNo')}:</strong> {apply.applyNo}</div>
            <div><strong>{t('storeDisplayGroupPlanogramApply.storeInfo')}:</strong> {apply.storeCode} - {apply.storeName}</div>
            <div><strong>{t('storeDisplayGroupPlanogramApply.displayGroupInfo')}:</strong> {apply.displayGroupCode} - {apply.displayGroupName}</div>
            <div><strong>{t('storeDisplayGroupPlanogramApply.applyReason')}:</strong> {apply.applyReason}</div>
          </div>
          
          <Form
            form={auditForm}
            layout="vertical"
            onFinish={handleAuditSubmit}
          >
            <Form.Item
              name="applyStatus"
              label={t('storeDisplayGroupPlanogramApply.auditResult')}
              rules={[{ required: true, message: t('storeDisplayGroupPlanogramApply.auditResultRequired') }]}
            >
              <Select placeholder={t('storeDisplayGroupPlanogramApply.auditResultPlaceholder')}>
                <Select.Option value={ApplyStatus.APPROVED}>
                  {t('storeDisplayGroupPlanogramApply.status.approved')}
                </Select.Option>
                <Select.Option value={ApplyStatus.REJECTED}>
                  {t('storeDisplayGroupPlanogramApply.status.rejected')}
                </Select.Option>
              </Select>
            </Form.Item>
            
            <Form.Item
              name="auditRemark"
              label={t('storeDisplayGroupPlanogramApply.auditRemark')}
              rules={[
                { max: 500, message: t('storeDisplayGroupPlanogramApply.auditRemarkMaxLength') }
              ]}
            >
              <Input.TextArea
                rows={4}
                placeholder={t('storeDisplayGroupPlanogramApply.auditRemarkPlaceholder')}
              />
            </Form.Item>
            
            <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
              <Space>
                <Button onClick={() => setAuditModalVisible(false)}>
                  {t('common.cancel')}
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={auditLoading}
                >
                  {t('common.submit')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default StoreDisplayGroupPlanogramApplyDetail;